/**
 * The ONE practice-forms renderer — `tasks/patient-forms.md` §5 / §13.4.
 *
 * Three hosts consume this component and a fourth is planned:
 *
 *   1. `PatientInviteWizardComponent` — splices ONE WIZARD STEP PER SECTION and renders this
 *      component with `[sectionId]` set to that section.
 *   2. `PatientFormComponent` (`/patient-form/:token`) — the standalone public page, which may show
 *      every section at once (`sectionId`/`sectionIndex` left null) or page through them itself.
 *   3. `PatientFormRequestListComponent` — the patient-file panel, `[readonly]="true"`.
 *   4. Later: the satisfaction survey page, which is how the two builders eventually become one.
 *
 * ── WHAT THIS COMPONENT OWNS, AND WHAT IT MUST NEVER OWN ─────────────────────────────────────
 *
 * OWNS: field rendering, `showIf` evaluation, required/format validation, section paging.
 *
 * NEVER OWNS: `HttpClient`, a route, a token, a service, or any knowledge that
 * `PatientFormRequest` exists. A host hands it a schema and answers; it hands back answers and
 * validity. The moment it learns about a token it stops being reusable by the wizard, and the
 * feature grows the second renderer that consent already has two of.
 *
 * ── ALL SCHEMA LOGIC COMES FROM `form-schema.util.ts` ────────────────────────────────────────
 *
 * `visibleQuestions` and `validateAnswers` are imported, never reimplemented. That file is the
 * client half of a cross-language pair verified against `utils/customFormSchema.js` over
 * `scripts/fixtures/custom-form-cases.json`. A `*ngIf` in the template that re-derived `showIf`,
 * or a `Validators.required` on a control, would be a THIRD implementation — and the one the
 * patient actually sees. So:
 *
 *   · visibility  = `visibleQuestions(schema, rawAnswers)`   — never a template condition
 *   · validity    = `validateAnswers(schema, answers).valid` — never `form.valid`
 *   · messages    = the util's `AnswerError.message`         — never a local string
 *
 * The controls carry NO Angular validators at all. `form.valid` is meaningless here on purpose:
 * there is exactly one authority on whether this form can be submitted, and the server shares it.
 *
 * ── THE PRUNE ────────────────────────────────────────────────────────────────────────────────
 *
 * A question hidden by `showIf` is REMOVED FROM THE EMITTED ANSWERS, not merely hidden. A patient
 * ticks "yes, I am on medication", names the medication, then changes the answer to "no". The
 * medication question comes off the screen; if its answer stayed in the map it would be filed in
 * the chart as a current medication for a patient who told us they take none. Hidden means gone.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subscription } from 'rxjs';

import {
  FormAnswers,
  FormOption,
  FormQuestion,
  FormSchema,
  FormSection,
} from '../../models/custom-form.model';
import { validateAnswers, visibleQuestions } from '../../util/form-schema.util';

/** One section as it is actually drawn: the section, and only its VISIBLE questions. */
export interface RenderedSection {
  section: FormSection;
  questions: FormQuestion[];
}

/** `scale` renders as discrete points; §13.1 gives no default, so an unbounded one is refused. */
const SCALE_DEFAULT_MIN = 1;
const SCALE_DEFAULT_MAX = 5;
const SCALE_MAX_POINTS = 21;

@Component({
  selector: 'app-dynamic-form-renderer',
  templateUrl: './dynamic-form-renderer.component.html',
  styleUrls: ['./dynamic-form-renderer.component.scss'],
})
export class DynamicFormRendererComponent implements OnInit, OnChanges, OnDestroy {

  /* ── The surface §13.4 freezes ─────────────────────────────────────────────────────────── */

  /** A `CustomFormVersion.schema`. Tolerates null/garbage by rendering nothing. */
  @Input() schema: FormSchema | null = null;

  /** The answers so far — a saved draft, a wizard's accumulated state, or `{}`. */
  @Input() value: FormAnswers = {};

  /** Read-only summary mode. Emits NOTHING: the patient-file panel is a viewer, not an editor. */
  @Input() readonly: boolean = false;

  /** The pruned answers. Emitted on every user change, never with a hidden question's answer. */
  @Output() valueChange = new EventEmitter<FormAnswers>();

  /** `validateAnswers` over the questions THIS INSTANCE renders. See `isSectionValid`. */
  @Output() validityChange = new EventEmitter<boolean>();

  /* ── Section paging (§5 "section paging", additive to §13.4) ───────────────────────────────
   *
   * The wizard splices one step per section and the standalone page may show them all, so neither
   * is hardcoded. Leave BOTH null and every visible section renders. Set one and only that section
   * renders — and `validityChange` then reports THAT SECTION's validity, which is what
   * `isStepValid` needs: step 1 must not be blocked by a blank question on step 3.
   *
   * `sectionId` is preferred over `sectionIndex` and wins when both are set. Visible sections are
   * recomputed as answers change — a whole section can disappear behind a `showIf` — so an INDEX
   * is a moving target while an id is stable. The wizard should hold ids.
   *
   * Visibility, pruning and the answers map are ALWAYS computed over the WHOLE schema regardless
   * of paging. A wizard step showing section 2 must not drop section 3's answers on the floor.
   *
   * ── PAGING DOES NOT RESET THE REVEALED ERRORS BY ITSELF ────────────────────────────────────
   *
   * A host that pages FORWARD should call `resetTouched()`, and a host that pages the patient TO
   * their errors must not. Both of those already exist, twelve lines apart, in
   * `patient-form.component.ts`:
   *
   *   · `goToSection()`  — the patient moved on, so the next section starts clean
   *   · `submit()`       — `markAllTouched()`, then jump to the first invalid section, where the
   *                        whole point is that the errors are showing when they arrive
   *
   * An automatic reset in a setter here would silently undo the second one and send the patient to
   * a section that looks fine, with nothing on screen saying what is wrong. The renderer cannot
   * tell the two apart from a changed id; the host always can. So the reset stays explicit.
   */
  @Input() sectionId: string | null = null;
  @Input() sectionIndex: number | null = null;

  /**
   * The currently VISIBLE sections, in document order, emitted whenever that list changes.
   *
   * This is how a host knows how many steps to splice and what to title them. A section whose
   * questions are all hidden by `showIf` is not in the list: a wizard step with nothing on it is
   * a dead end the patient cannot understand.
   */
  @Output() sectionsChange = new EventEmitter<FormSection[]>();

  /* ── Template state ───────────────────────────────────────────────────────────────────────── */

  form: FormGroup;

  /** Every visible section (paging ignored). */
  sections: RenderedSection[] = [];

  /** The sections this instance draws — `sections`, or the single paged one. */
  renderedSections: RenderedSection[] = [];

  /* ── Internals ────────────────────────────────────────────────────────────────────────────── */

  private showAllErrorsFlag = false;
  private answersMap: FormAnswers = {};
  private errorByQuestion: { [questionId: string]: string } = {};
  private matchers: { [questionId: string]: ErrorStateMatcher } = {};
  private scalePointCache: { [questionId: string]: number[] } = {};
  private formSub?: Subscription;
  private built = false;
  private destroyed = false;

  /** Identity of the object we last emitted, so a host's `[(value)]` echo does not rebuild. */
  private lastEmitted: FormAnswers | null = null;
  private lastValidity: boolean | null = null;
  private lastVisibleSignature: string | null = null;
  private lastSectionSignature: string | null = null;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({});
  }

  /* ── Lifecycle ────────────────────────────────────────────────────────────────────────────── */

  ngOnInit(): void {
    if (!this.built) this.build();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A `value` echo of what we ourselves just emitted is not a change: rebuilding the controls on
    // it would reset the field the patient is typing in on every keystroke.
    const valueEchoed = !!changes['value'] && this.value === this.lastEmitted;
    const structural = !!changes['schema'] || !!changes['readonly'] || (!!changes['value'] && !valueEchoed);

    if (structural) {
      this.build();
      return;
    }
    if (this.built && (changes['sectionId'] || changes['sectionIndex'])) {
      this.recompute('init');
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.formSub?.unsubscribe();
  }

  /* ── Public read surface — for a host that cannot wait for an event ───────────────────────── */

  /** The pruned answers as they stand. Same object the last `valueChange` carried. */
  get answers(): FormAnswers {
    return this.answersMap;
  }

  /** Validity of what THIS instance renders — the same value `validityChange` last emitted. */
  get valid(): boolean {
    return this.lastValidity === true;
  }

  /** Every visible section, whatever this instance happens to be paging to. */
  get visibleSections(): FormSection[] {
    return this.sections.map((rendered) => rendered.section);
  }

  /**
   * Is one section complete? The wizard's `isStepValid` for a spliced form step.
   *
   * An unknown or currently invisible section is VALID — there is nothing on it to answer, and a
   * wizard must not be wedged on a step that is not being shown.
   */
  isSectionValid(sectionId: string): boolean {
    const rendered = this.sections.filter((entry) => entry.section && entry.section.id === sectionId)[0];
    if (!rendered) return true;
    return this.scopeIsValid([rendered]);
  }

  /**
   * Is every outstanding error currently revealed? READ-ONLY on purpose.
   *
   * `markAllTouched()` and `resetTouched()` are the only two ways to move it. A public mutable
   * flag beside a method that sets it is two ways to do one thing, and the second one is the one
   * a host reaches into.
   */
  get showAllErrors(): boolean {
    return this.showAllErrorsFlag;
  }

  /**
   * Reveal every outstanding error. Hosts call this when the patient presses Submit or Next.
   *
   * Counterpart: `resetTouched()`.
   */
  markAllTouched(): void {
    this.showAllErrorsFlag = true;
    this.form.markAllAsTouched();
  }

  /**
   * Un-reveal them. The counterpart to `markAllTouched()`.
   *
   * A HOST CALLS THIS ON A PAGING CHANGE, so that errors revealed on one section do not greet the
   * patient on the next: pressing Next on an unfinished section 2 reveals its errors, and without
   * this the patient arrives at section 3 to a wall of red for questions they have not reached.
   *
   * It clears the flag AND the touched state `markAllTouched()` set — clearing only the flag would
   * leave every control touched, which reveals the same errors by the other half of the rule in
   * `shouldShowError`.
   *
   * It does NOT clear `dirty`, so a question the patient actually typed into and left invalid goes
   * on showing its own error. That is not the reported problem: the complaint is about questions
   * they have never reached, and a field they edited is not one of those.
   *
   * Answers, validity, the visible-section list and the paging are all untouched — nothing here
   * emits, and the form is not rebuilt.
   */
  resetTouched(): void {
    this.showAllErrorsFlag = false;
    this.form.markAsUntouched();
  }

  /* ── Building ─────────────────────────────────────────────────────────────────────────────── */

  /**
   * One control per non-`statictext` question in the WHOLE schema, hidden ones included.
   *
   * Controls exist for hidden questions so that a patient who reveals a question, answers it,
   * hides it and reveals it again is not silently made to answer twice — the draft still holds it.
   * The prune happens on the way OUT, in `recompute`, which is the only place it can be correct.
   *
   * Values are written RAW, with no coercion. A draft that arrived over the wire holding `'3'`
   * where a scale wants `3` must reach `validateAnswers` as `'3'`, because that is the value the
   * server will see too. A renderer that quietly repaired it would disagree with the endpoint
   * about whether the submission is acceptable — the one disagreement this module exists to
   * prevent (§13.5, "loose `eq` numeric comparison").
   */
  private build(): void {
    this.formSub?.unsubscribe();

    const source: FormAnswers = this.isObject(this.value) ? this.value : {};
    const controls: { [key: string]: any } = {};

    for (const question of this.allQuestions()) {
      if (question.type === 'statictext') continue;
      if (typeof question.id !== 'string' || question.id === '') continue;
      const answer = Object.prototype.hasOwnProperty.call(source, question.id) ? source[question.id] : null;
      controls[question.id] = [{ value: answer === undefined ? null : answer, disabled: !!this.readonly }];
    }

    this.form = this.fb.group(controls);
    this.matchers = {};
    this.scalePointCache = {};
    this.showAllErrorsFlag = false;
    this.built = true;

    this.formSub = this.form.valueChanges.subscribe(() => this.recompute('live'));
    this.recompute('init');
  }

  /**
   * Recompute visibility, the pruned answers, the errors and the paging — then emit.
   *
   * `mode`:
   *   'live' — a user change. Emitting synchronously here is safe: we are inside an event handler,
   *            not inside a change-detection pass.
   *   'init' — a build or a paging change. Emissions are deferred to a microtask, because a host
   *            binding `[value]` and `(valueChange)` would otherwise be reassigning a bound
   *            expression during its own change detection —
   *            `ExpressionChangedAfterItHasBeenCheckedError` in dev mode.
   */
  private recompute(mode: 'init' | 'live'): void {
    const raw: FormAnswers = this.form.getRawValue() || {};

    // Visibility is evaluated against the RAW map — hidden answers included. That is deliberate:
    // `visibleQuestions` implements the chained rule (a hidden controller hides its dependants)
    // precisely so a stale answer to a question no longer being asked cannot reveal anything.
    const visible = visibleQuestions((this.schema || { schemaVersion: 1, sections: [] }) as FormSchema, raw);
    const visibleSet = new Set<FormQuestion>(visible);

    // ── the prune ──
    const answers: FormAnswers = {};
    for (const question of visible) {
      if (question.type === 'statictext') continue;
      if (typeof question.id !== 'string' || question.id === '') continue;
      const answer = raw[question.id];
      // Strictly-empty values are simply omitted rather than sent as null. Whitespace is NOT
      // trimmed away here: `form-schema.util` is the sole authority on what counts as answered,
      // and a renderer that trimmed first would be making that call in a second place.
      if (answer === undefined || answer === null) continue;
      if (answer === '') continue;
      if (Array.isArray(answer) && answer.length === 0) continue;
      answers[question.id] = answer;
    }
    this.answersMap = answers;

    // ── errors: the util's messages, verbatim, at most one per question ──
    const result = validateAnswers(
      (this.schema || { schemaVersion: 1, sections: [] }) as FormSchema,
      answers,
    );
    const errorMap: { [questionId: string]: string } = {};
    for (const error of result.errors) errorMap[error.questionId] = error.message;
    this.errorByQuestion = errorMap;

    // ── sections: rebuilt only when the visible set actually changed, so `trackBy` has nothing to
    //    diff on a keystroke and a half-drawn signature pad is never torn down and recreated ──
    const signature = visible.map((question) => question.id).join('');
    if (signature !== this.lastVisibleSignature) {
      this.lastVisibleSignature = signature;
      this.sections = (this.schema && Array.isArray(this.schema.sections) ? this.schema.sections : [])
        .filter((section) => this.isObject(section))
        .map((section) => ({
          section,
          questions: (Array.isArray(section.questions) ? section.questions : [])
            .filter((question) => visibleSet.has(question)),
        }))
        .filter((rendered) => rendered.questions.length > 0);
    }

    this.renderedSections = this.pageSections();
    const valid = this.scopeIsValid(this.renderedSections);

    this.emitState(mode, answers, valid);
  }

  /** Which sections this instance draws. `sectionId` wins; an out-of-range page draws nothing. */
  private pageSections(): RenderedSection[] {
    if (typeof this.sectionId === 'string' && this.sectionId !== '') {
      return this.sections.filter((rendered) => rendered.section && rendered.section.id === this.sectionId);
    }
    if (typeof this.sectionIndex === 'number' && this.sectionIndex >= 0) {
      const rendered = this.sections[this.sectionIndex];
      return rendered ? [rendered] : [];
    }
    return this.sections;
  }

  /**
   * Is every question in this scope free of errors?
   *
   * When the scope is the whole form this is exactly `validateAnswers(...).valid` — the errors are
   * keyed on visible questions, and every visible question belongs to a visible section. Paging
   * narrows WHICH errors count, never how they are computed.
   */
  private scopeIsValid(scope: RenderedSection[]): boolean {
    const ids = new Set<string>();
    for (const rendered of scope) {
      for (const question of rendered.questions) {
        if (typeof question.id === 'string') ids.add(question.id);
      }
    }
    for (const questionId of Object.keys(this.errorByQuestion)) {
      if (ids.has(questionId)) return false;
    }
    return true;
  }

  private emitState(mode: 'init' | 'live', answers: FormAnswers, valid: boolean): void {
    const sectionSignature = this.sections
      .map((rendered) => (rendered.section ? rendered.section.id : ''))
      .join('');
    const sectionsChanged = sectionSignature !== this.lastSectionSignature;
    const validityChanged = valid !== this.lastValidity;

    // On a build the answers are only worth emitting if the prune actually changed them --
    // otherwise the host is handed back exactly what it just passed in.
    const answersChanged = mode === 'live' || !this.sameAnswers(answers, this.value);

    // Recorded synchronously, whatever happens next, so the read getters are truthful the instant
    // `recompute` returns for a host that reads rather than subscribes.
    this.lastSectionSignature = sectionSignature;
    this.lastValidity = valid;

    // Read-only mode emits NOTHING: the patient-file panel is a viewer, not an editor.
    if (this.readonly) return;

    const fire = () => {
      if (this.destroyed) return;
      if (sectionsChanged) this.sectionsChange.emit(this.visibleSections);
      if (answersChanged) {
        this.lastEmitted = answers;
        this.valueChange.emit(answers);
      }
      if (validityChanged) this.validityChange.emit(valid);
    };

    if (mode === 'live') {
      fire();
    } else {
      // Deferred: see `recompute`'s note on ExpressionChangedAfterItHasBeenCheckedError.
      Promise.resolve().then(fire);
    }
  }

  /** Shallow, order-independent comparison of two answer maps. */
  private sameAnswers(a: FormAnswers, b: FormAnswers): boolean {
    if (!this.isObject(b)) return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      const left = a[key];
      const right = b[key];
      if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) return false;
        if (left.length !== right.length) return false;
        for (let i = 0; i < left.length; i++) if (left[i] !== right[i]) return false;
        continue;
      }
      if (left !== right) return false;
    }
    return true;
  }

  private allQuestions(): FormQuestion[] {
    const out: FormQuestion[] = [];
    if (!this.schema || !Array.isArray(this.schema.sections)) return out;
    for (const section of this.schema.sections) {
      if (!this.isObject(section) || !Array.isArray(section.questions)) continue;
      for (const question of section.questions) {
        if (this.isObject(question)) out.push(question);
      }
    }
    return out;
  }

  private isObject(value: any): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /* ── Template helpers ─────────────────────────────────────────────────────────────────────── */

  get hasContent(): boolean {
    return this.renderedSections.length > 0;
  }

  trackBySection = (_index: number, rendered: RenderedSection): string =>
    (rendered.section && rendered.section.id) || String(_index);

  trackByQuestion = (_index: number, question: FormQuestion): string => question.id || String(_index);

  labelId(question: FormQuestion): string {
    return `dfr-label-${question.id}`;
  }

  helpId(question: FormQuestion): string {
    return `dfr-help-${question.id}`;
  }

  /** The util's message for this question, or ''. Never a locally invented string. */
  errorFor(question: FormQuestion): string {
    return this.errorByQuestion[question.id] || '';
  }

  /** Show it once the patient has been near the field, or once a host asked for everything. */
  shouldShowError(question: FormQuestion): boolean {
    if (!this.errorFor(question)) return false;
    if (this.showAllErrorsFlag) return true;
    const control = this.form.get(question.id);
    return !!control && (control.touched || control.dirty);
  }

  /**
   * Material only renders `<mat-error>` when the field is in an error state, and our controls
   * carry no Angular validators by design — so the error state is delegated back to the util's
   * verdict through a per-question matcher.
   */
  matcherFor(question: FormQuestion): ErrorStateMatcher {
    const id = question.id;
    if (!this.matchers[id]) {
      this.matchers[id] = { isErrorState: () => this.shouldShowError(question) } as ErrorStateMatcher;
    }
    return this.matchers[id];
  }

  /** `scale` points, inclusive of both ends. §13.1 leaves min/max optional; 1–5 is the default. */
  scalePoints(question: FormQuestion): number[] {
    const id = question.id;
    if (this.scalePointCache[id]) return this.scalePointCache[id];

    let min = typeof question.min === 'number' && isFinite(question.min) ? Math.round(question.min) : SCALE_DEFAULT_MIN;
    let max = typeof question.max === 'number' && isFinite(question.max) ? Math.round(question.max) : SCALE_DEFAULT_MAX;
    if (max < min) { const swap = min; min = max; max = swap; }
    if (max - min + 1 > SCALE_MAX_POINTS) max = min + SCALE_MAX_POINTS - 1;

    const points: number[] = [];
    for (let n = min; n <= max; n++) points.push(n);
    this.scalePointCache[id] = points;
    return points;
  }

  options(question: FormQuestion): FormOption[] {
    return Array.isArray(question.options) ? question.options : [];
  }

  /* ── checkbox: one control holding `string[]`, not one control per option ──────────────────── */

  isChecked(question: FormQuestion, option: FormOption): boolean {
    const current = this.form.get(question.id)?.value;
    return Array.isArray(current) && current.indexOf(option.value) !== -1;
  }

  toggleOption(question: FormQuestion, option: FormOption, checked: boolean): void {
    const control = this.form.get(question.id);
    if (!control) return;
    const current: any[] = Array.isArray(control.value) ? control.value.slice() : [];
    const at = current.indexOf(option.value);
    if (checked && at === -1) current.push(option.value);
    if (!checked && at !== -1) current.splice(at, 1);
    control.markAsTouched();
    control.setValue(current);
  }

  /** `SignaturePadComponent` emits a base64 data URL on `done` and '' on `cleared` (§13.1). */
  onSignature(question: FormQuestion, dataUrl: any): void {
    const control = this.form.get(question.id);
    if (!control) return;
    control.markAsTouched();
    control.setValue(typeof dataUrl === 'string' && dataUrl !== '' ? dataUrl : null);
  }

  /* ── read-only summary ────────────────────────────────────────────────────────────────────── */

  hasAnswer(question: FormQuestion): boolean {
    return Object.prototype.hasOwnProperty.call(this.answersMap, question.id);
  }

  answerOf(question: FormQuestion): any {
    return this.answersMap[question.id];
  }

  /** The answer as a person reads it: option TEXT, not option value; 'Yes'/'No', not true/false. */
  displayAnswer(question: FormQuestion): string {
    const answer = this.answerOf(question);
    if (answer === undefined || answer === null) return '';

    if (question.type === 'yesno') {
      if (answer === true || answer === 'true') return 'Yes';
      if (answer === false || answer === 'false') return 'No';
      return String(answer);
    }

    const textFor = (value: any): string => {
      const option = this.options(question).filter((candidate) => candidate.value === value)[0];
      return option ? option.text : String(value);
    };

    if (Array.isArray(answer)) return answer.map(textFor).join(', ');
    if (question.type === 'radio' || question.type === 'select') return textFor(answer);
    return String(answer);
  }
}
